import axios from "axios";
import { useCallback, useContext, useEffect, useState } from "react";
import styled from "styled-components";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";

import { FilterContext } from "./Context/FilterContext";
import NoResultsFound from "./NoResultsFound";
import PropertyCard from "./PropertyCard";

const PropertiesGridstyle = styled.section`
  background-position: bottom center;
  margin-top: 24px;
  margin-bottom: 42px;

  &.sticky {
    padding-top: 380px;
  }

  .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .properties-card-container {
    width: 100%;
    display: grid;
    grid-gap: 24px;
    grid-auto-flow: row dense;
    grid-template-columns: 1fr;

    @media only screen and (min-width: 768px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media only screen and (min-width: 992px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media only screen and (min-width: 1440px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }
  .preloader {
    width: 100%;
    display: block;
    img {
      display: block;
      margin: 30px auto;
    }
  }
  .load-more-container {
    padding: 30px 0;
    .container {
      display: block;
      text-align: center;
      color: var(--color-secondary);

      button {
        display: block;
        margin: 16px auto;
        max-width: 200px;
        //border: 1px solid var(--color-secondary);
        background-color: var(--color-primary);
        border-radius: 100px;
        border: none;
        padding: 16px 32px;
        color: #fff;
        cursor: pointer;
      }
    }
  }
`;

const PAGE_SIZE = 20;

const getProperties = (filters, limit, offset, cb) => {
  const url = `${process.env.REACT_APP_API_URL}/properties/`;
  const params = new URLSearchParams();
  Object.keys(filters || {}).forEach((key) => {
    let apiKey;
    switch (key) {
      case "transactionType":
        apiKey = "listing__transaction_type__in";
        break;
      case "min_price":
        apiKey = "listing__sale_price__gte";
        break;
      case "max_price":
        apiKey = "listing__sale_price__lte";
        break;
      case "propertyType":
        apiKey = "type";
        break;
      case "collection":
        apiKey = "collections__slug__in";
        break;
      case "location":
        apiKey = "city__contains";
        break;
      case "searchKeyword":
        apiKey = "search";
        break;
      case "bedrooms":
        apiKey = "bedroom_count__gte";
        break;
      case "bathrooms":
        apiKey = "bathroom_count__gte";
        break;
      case "parking":
        apiKey = "parking_space_count__gte";
        break;
      case "floorAreaMin":
        apiKey = "floor_area__gte";
        break;
      case "floorAreaMax":
        apiKey = "floor_area__lte";
        break;
      case "lotAreaMin":
        apiKey = "lot_area__gte";
        break;
      case "lotAreaMax":
        apiKey = "lot_area__lte";
        break;
      case "furnishing":
        apiKey = "furnishing_status__in";
        break;
      default:
        apiKey = key;
    }

    if (filters[key] !== "" && filters[key] !== undefined) {
      params.append(apiKey, filters[key]);
    }
  });

  params.append("ordering", "-listing__sale_price");
  params.append("limit", limit);
  params.append("offset", offset);

  axios
    .get(url, { params })
    .then((res) => cb(res.data.results, res.data.count, null))
    .catch((err) => cb(null, null, err));
};

const PropertiesGrid = ({ activeClass }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [listings, setListings] = useState([]);
  const filters = useContext(FilterContext);
  const [totalProperties, setTotalProperties] = useState(0);

  const fetchPage = useCallback((currentFilters, currentListings) => {
    setIsLoading(true);
    getProperties(currentFilters, PAGE_SIZE, currentListings.length, (data, totalCount, err) => {
      if (err) {
        console.error(err);
      } else {
        setListings([...currentListings, ...data]);
        setTotalProperties(totalCount);
      }
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    setListings([]);
    fetchPage(filters, []);
  }, [filters, fetchPage]);

  const handleLoadMore = () => {
    fetchPage(filters, listings);
  };

  return (
    <>
      <PropertiesGridstyle className={activeClass}>
        {isLoading && (
          <div className="container">
            <div className="properties-card-container">
              <SkeletonTheme baseColor="#ebebeb" highlightColor="#D3D3D3">
                <Skeleton height={356} />
                <Skeleton height={356} />
                <Skeleton height={356} />
                <Skeleton height={356} />
                <Skeleton height={356} />
                <Skeleton height={356} />
                <Skeleton height={356} />
                <Skeleton height={356} />
              </SkeletonTheme>
            </div>
          </div>
        )}
        <div className="container">
          <div className="properties-card-container">
            {listings.map((listing, idx) => (
              <PropertyCard listing={listing} key={idx} />
            ))}
          </div>
        </div>

        {!isLoading && listings.length === 0 && (
          <NoResultsFound count={listings.length} />
        )}
        {!isLoading && listings.length > 0 && listings.length < totalProperties && (
          <div className="load-more-container">
            <div className="container">
              {listings.length} of {totalProperties} properties
              <button onClick={handleLoadMore}>Load More</button>
            </div>
          </div>
        )}
        {!isLoading && listings.length > 0 && listings.length >= totalProperties && (
          <div className="load-more-container">
            <div className="container">
              <p>
                {" "}
                {listings.length} of {totalProperties} properties
              </p>
              You have reached the end
            </div>
          </div>
        )}
      </PropertiesGridstyle>
    </>
  );
};

export default PropertiesGrid;
